import React, { useState } from 'react';

import CardDialog from '~/components/CardDialog';
import { Heading } from '~/components/core';
import { AddItemButton } from '~/components/core/NewDesignSystem/Buttons/AddItemButton';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import type { UserModel } from '~/components/types/user-types';
import useDataFetcher from '~/components/useDataFetcher';
import type { AuditedConfigurationChangeList } from '~/server_shared/openapi/data-contracts';

// import type { ChangeRequest } from '~/server_shared/openapi/data-contracts';
import ChangeRequestsTable from './ChangeRequestsTable';
import CreateChangeRequestDialog from './CreateChangeRequestDialog';
import type { ChangeRequest } from './types';

interface ChangeRequestsProps {
  configurationChanges?: AuditedConfigurationChangeList;
  users?: UserModel[];
  isLoadingChanges: boolean;
  isErrorChanges: boolean;
  isLoadingUsers: boolean;
  isErrorUsers: boolean;
  reloadConfigurationChanges: () => void;
  organization: Record<string, unknown>;
}

const ChangeRequests: React.FC<ChangeRequestsProps> = ({
  configurationChanges,
  users,
  isLoadingChanges,
  isErrorChanges,
  isLoadingUsers,
  isErrorUsers,
  reloadConfigurationChanges,
  organization,
}) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const {
    isLoading: isLoadingChangeRequests,
    isError: isErrorChangeRequests,
    data: changeRequestsResponse,
    reloadData: reloadChangeRequests,
  } = useDataFetcher<{ change_requests: ChangeRequest[]; total_count: number }>(
    `/sysconfig/api/v1/organizations/${organization.id}/change_requests`
  );

  const changeRequests = changeRequestsResponse?.change_requests || [];

  const displayLoading =
    isLoadingUsers ||
    isErrorUsers ||
    !users ||
    isLoadingChangeRequests ||
    isErrorChangeRequests ||
    !changeRequestsResponse;

  return (
    <CardDialog noCardTitle>
      <div className="flex items-center justify-between">
        <Heading className="my-20">Change Requests</Heading>
        <AddItemButton text="ADD CHANGE REQUEST" onClick={() => setIsCreateDialogOpen(true)} variant="contained" />
      </div>
      {displayLoading ? (
        <SkeletonTable rowsCount={5} columnsCount={10} isError={isErrorChangeRequests || isErrorUsers} />
      ) : (
        <ChangeRequestsTable
          rows={changeRequests}
          users={users}
          organization={organization}
          onChangeRequestUpdate={reloadChangeRequests}
        />
      )}
      {isCreateDialogOpen && (
        <CreateChangeRequestDialog
          onClose={() => setIsCreateDialogOpen(false)}
          organization={organization}
          configurationChanges={configurationChanges}
          users={users}
          isLoadingChanges={isLoadingChanges}
          isErrorChanges={isErrorChanges}
          isLoadingUsers={isLoadingUsers}
          isErrorUsers={isErrorUsers}
          onSuccess={() => {
            setIsCreateDialogOpen(false);
            reloadChangeRequests();
            reloadConfigurationChanges();
          }}
        />
      )}
    </CardDialog>
  );
};

export default ChangeRequests;
